import { ArrowLeftOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Card, Modal, Skeleton, Space, Switch, Tabs, Tag, Typography, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTabHistory } from '../utils/useTabHistory';
import { errorMessage } from '../api/client';
import { useDealer, useDeleteDealer, useUpdateDealerStatus } from '../api/hooks';
import { offerDeactivateOrForceDelete } from '../utils/historyAwareDelete';
import QuickShareButton from '../components/common/QuickShareButton';
import { usePartyContact } from '../components/common/usePartyContact';
import { accountOpenedMessage } from '../utils/quickShare';
import FixedInvestmentsTab from '../components/common/FixedInvestmentsTab';
import FixedLedgerTab from '../components/common/FixedLedgerTab';
import FixedReceiptsTab from '../components/common/FixedReceiptsTab';
import FixedWithdrawalsTab from '../components/common/FixedWithdrawalsTab';
import DealerInvestmentsTab from '../components/dealer/DealerInvestmentsTab';
import DealerLedgerTab from '../components/dealer/DealerLedgerTab';
import DealerProfileTab from '../components/dealer/DealerProfileTab';
import DealerProfitTab from '../components/dealer/DealerProfitTab';
import DealerReceiptsTab from '../components/dealer/DealerReceiptsTab';
import DealerWithdrawalsTab from '../components/dealer/DealerWithdrawalsTab';
import { useTranslation } from 'react-i18next';

/**
 * A single dealer's page — deliberately built exactly like InvestorDetailPage: same header layout,
 * same tab order (Profile, Investments, Profit, Withdrawals, Ledger, Receipts), same Active switch.
 * The two sides of the money should read the same way, not need two different mental models.
 */
export default function DealerDetailPage() {
  const { t } = useTranslation();
  const tabs = useTabHistory('profile');
  const { id } = useParams<{ id: string }>();
  const dealerId = Number(id);
  const navigate = useNavigate();
  const { data } = useDealer(dealerId);
  const updateStatus = useUpdateDealerStatus(dealerId);
  const deleteDealer = useDeleteDealer();
  const contact = usePartyContact('Dealer', dealerId);
  const isFixed = data?.dealType === 'FixedReturn';

  const toggleStatus = async (active: boolean) => {
    try {
      await updateStatus.mutateAsync(active ? 'Active' : 'Inactive');
      message.success(`My investment ${active ? 'activated' : 'deactivated'}.`);
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  const onDelete = () => {
    if (!data) return;
    Modal.confirm({
      title: `Delete ${data.dealerCode} — ${data.fullName}?`,
      content:
        'This only succeeds if the dealer has no transaction history. If they have any investments, withdrawals or profit payments, you will be offered the choice to deactivate instead.',
      okText: 'Delete permanently',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteDealer.mutateAsync({ id: dealerId });
          message.success('Dealer deleted.');
          navigate('/dealers');
        } catch (err) {
          if (errorMessage(err).includes('has transaction history')) {
            offerDeactivateOrForceDelete({
              code: data.dealerCode,
              fullName: data.fullName,
              onDeactivate: () => updateStatus.mutateAsync('Inactive'),
              onForceDelete: async () => {
                await deleteDealer.mutateAsync({ id: dealerId, force: true });
                navigate('/dealers');
              },
              deactivatedMessage: 'Dealer deactivated.',
              deletedMessage: 'Dealer and all transaction history deleted.',
            });
          } else {
            Modal.error({ title: 'Could not delete this dealer', content: errorMessage(err) });
          }
        }
      },
    });
  };

  if (!data) return <Skeleton active avatar paragraph={{ rows: 8 }} />;

  return (
    <>
      <Space style={{ marginBottom: 16, flexWrap: 'wrap', justifyContent: 'space-between', width: '100%' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            {t('common.back')}
          </Button>
          <Typography.Title level={4} style={{ margin: 0 }}>
            <span data-no-translate>{data.dealerCode} — {data.fullName}</span>
          </Typography.Title>
          <Tag color={data.status === 'Active' ? 'green' : 'default'}>{data.status}</Tag>
        </Space>

        <Space>
          {contact.ready && (
            <QuickShareButton
              variant="default"
              label={t('page.sendAccountDetails')}
              phone={contact.phone}
              recipientName={contact.name}
              message={accountOpenedMessage(contact)}
            />
          )}
          <span>{t('common.active')}</span>
          <Switch checked={data.status === 'Active'} loading={updateStatus.isPending} onChange={toggleStatus} />
          <Button danger icon={<DeleteOutlined />} loading={deleteDealer.isPending} onClick={onDelete}>
            Delete
          </Button>
        </Space>
      </Space>

      <Card>
        <Tabs
          activeKey={tabs.activeKey}
          onChange={tabs.onChange}
          items={[
            { key: 'profile', label: t('tabs.profile'), children: <DealerProfileTab dealerId={dealerId} /> },
            ...(isFixed
              ? [
                  {
                    key: 'investments',
                    label: t('tabs.investments'),
                    children: <FixedInvestmentsTab partyType="Dealer" partyId={dealerId} canWrite />,
                  },
                  {
                    key: 'withdrawals',
                    label: t('tabs.withdrawals'),
                    children: <FixedWithdrawalsTab partyType="Dealer" partyId={dealerId} canWrite />,
                  },
                  {
                    key: 'ledger',
                    label: t('tabs.ledger'),
                    children: <FixedLedgerTab partyType="Dealer" partyId={dealerId} />,
                  },
                  {
                    key: 'receipts',
                    label: t('tabs.receipts'),
                    children: <FixedReceiptsTab partyType="Dealer" partyId={dealerId} />,
                  },
                ]
              : [
                  { key: 'investments', label: t('tabs.investments'), children: <DealerInvestmentsTab dealerId={dealerId} /> },
                  { key: 'profit', label: t('tabs.profit'), children: <DealerProfitTab dealerId={dealerId} /> },
                  { key: 'withdrawals', label: t('tabs.withdrawals'), children: <DealerWithdrawalsTab dealerId={dealerId} /> },
                  { key: 'ledger', label: t('tabs.ledger'), children: <DealerLedgerTab dealerId={dealerId} /> },
                  { key: 'receipts', label: t('tabs.receipts'), children: <DealerReceiptsTab dealerId={dealerId} /> },
                ]),
          ]}
        />
      </Card>
    </>
  );
}
